import * as React from "react";

/**
 * Shell-title boundary — the single source of truth for *who renders the page
 * title*.
 *
 * The modes IA mounts standalone pages (each of which renders its OWN
 * `PageHeader`/`PageNav` title) INSIDE a `ModeShell`, which renders the canonical
 * `Mode · Domain` title. That stacked two page titles on every `?area=` route
 * (Product · Projects + "Projects", Product · Roadmap + "Roadmap", Operations ·
 * Audits + "Best-in-class audits", every CRM/Hiring view, …) — the
 * "multiple titles" bug, systemic across every mounted domain view.
 *
 * `ModeShell` wraps its content in `<ShellTitleBoundary>` to declare "I already
 * render the page title." Any `PageHeader`/`PageNav` rendered beneath then
 * suppresses its own redundant heading (keeping its toolbar/tabs/subtitle), so a
 * mode/domain route shows exactly ONE page title — the shell's — while the same
 * page visited standalone (outside a `ModeShell`) renders its title as before.
 *
 * This is the documented `PageHeader` convention ("omit the title on pages whose
 * name is already shown by the `PageNav`") made automatic, so a new mounted view
 * never has to remember it.
 */
const ShellOwnsTitleContext = React.createContext(false);

/** Declares that an enclosing shell already renders the page title, so nested
 *  `PageHeader`/`PageNav` headings should defer to it. Wrap ONLY the shell's
 *  content — never the shell's own title bar. */
export function ShellTitleBoundary({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <ShellOwnsTitleContext.Provider value={true}>
      {children}
    </ShellOwnsTitleContext.Provider>
  );
}

/** True when a `ShellTitleBoundary` ancestor already renders the page title. */
export function useShellOwnsTitle(): boolean {
  return React.useContext(ShellOwnsTitleContext);
}

/** Render-prop gate for SERVER components (e.g. `PageHeader`): drops `children`
 *  when an enclosing shell owns the page title, keeping the host component a
 *  plain server component (only this leaf is a client boundary). */
export function HideWhenShellTitled({
  children,
}: {
  children: React.ReactNode;
}) {
  return useShellOwnsTitle() ? null : <>{children}</>;
}

/**
 * Shell title-ACTIONS portal — the generalization of the Work▸Issues toolbar
 * hoist to ANY mounted page. When a page renders inside a `ModeShell`, its own
 * title-row actions (its `PageHeader` `toolbar` — create/filter/view controls,
 * often client-stateful) should sit INLINE with the shell's serif title, not
 * orphaned in the body below it. A mounted page's `<ShellActionsPortal>` lifts its
 * toolbar up into the `<ShellActionsSlot>` the shell renders in its
 * `SectionTitleBar`. Standalone (no provider) the toolbar just renders inline.
 *
 * The lifted node lives in a tiny EXTERNAL STORE, not in provider state, and the
 * portal re-publishes it on every render. The original version snapshotted
 * `children` once in a mount-only effect, so a hoisted control that owned client
 * state went STALE the moment it changed: the Goals ledger's kind filter
 * re-filtered the list below while its own trigger kept reading "All kinds · 7"
 * (Matt, 2026-07-20). Publishing on every render keeps it live; the store (rather
 * than provider state) is what makes that safe — only the `<ShellActionsSlot>`
 * subscribes, so a republish re-renders the slot and NOT the portal that fed it,
 * which is the re-render loop this seam was avoiding in the first place.
 */
interface ShellActionsStore {
  get: () => React.ReactNode;
  set: (node: React.ReactNode) => void;
  subscribe: (listener: () => void) => () => void;
}

function createShellActionsStore(): ShellActionsStore {
  let node: React.ReactNode = null;
  const listeners = new Set<() => void>();
  return {
    get: () => node,
    set: (next) => {
      if (next === node) return;
      node = next;
      for (const listener of listeners) listener();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

const ShellActionsStoreContext = React.createContext<ShellActionsStore | null>(
  null,
);

/** Nothing is ever lifted during SSR (the portal publishes from an effect), so the
 *  server snapshot is a stable null. */
const emptySnapshot = () => null;
const noopSubscribe = () => () => {};

/** Wrap the shell region that contains BOTH its title bar (the `<ShellActionsSlot>`)
 *  and its mounted children (the `<ShellActionsPortal>`), so a page can lift its
 *  actions up into the title. */
export function ShellActionsProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [store] = React.useState(createShellActionsStore);
  return (
    <ShellActionsStoreContext.Provider value={store}>
      {children}
    </ShellActionsStoreContext.Provider>
  );
}

/** The slot in the shell's title bar where a mounted page's hoisted actions land.
 *  Renders nothing until a page portals its toolbar up, and re-renders on its own
 *  whenever that page republishes — the slot is the only subscriber. */
export function ShellActionsSlot() {
  const store = React.useContext(ShellActionsStoreContext);
  const node = React.useSyncExternalStore(
    store?.subscribe ?? noopSubscribe,
    store?.get ?? emptySnapshot,
    emptySnapshot,
  );
  return <>{node}</>;
}

/** Rendered by `PageHeader` (and any client surface with title-row controls):
 *  under a shell that owns the title, lift `children` up into the shell's
 *  `<ShellActionsSlot>` (and render nothing here); standalone, render them inline.
 *  `children` (not a named prop) so a server `PageHeader` can pass a
 *  server-component toolbar through the boundary. */
export function ShellActionsPortal({
  children,
}: {
  children: React.ReactNode;
}) {
  const store = React.useContext(ShellActionsStoreContext);
  const shelled = useShellOwnsTitle();
  const lifted = Boolean(store) && shelled;
  const childrenRef = React.useRef(children);
  childrenRef.current = children;
  // Republish after EVERY render (no dep array) so the lifted controls track this
  // page's props/state instead of freezing at their mount-time values…
  React.useEffect(() => {
    if (lifted) store!.set(childrenRef.current);
  });
  // …and clear the slot only on a real unmount, so a republish never blanks it
  // mid-flight (which would tear down the lifted controls' own state).
  React.useEffect(() => {
    if (!lifted) return;
    return () => store!.set(null);
  }, [store, lifted]);
  return lifted ? null : <>{children}</>;
}
